import React, { useState, useEffect } from 'react';
import { apiService, handleApiError } from '../services/api';
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  FunnelIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ConfidenceBadge = ({ confidence }) => {
  const getConfidenceClass = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'medium':
      case 'med':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getConfidenceClass(confidence)}`}>
      {confidence || 'Unknown'}
    </span>
  );
};

const MatchBasisBadge = ({ matchBasis }) => {
  const getBasisClass = (basis) => {
    switch (basis?.toLowerCase()) {
      case 'citation':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'terms':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'semantic':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getBasisClass(matchBasis)}`}>
      {matchBasis || 'Unknown'}
    </span>
  );
};

const ProposalCard = ({ link, onViewDocument }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onViewDocument(link.document_id)}
          className="text-sm font-medium text-vodafone-600 hover:text-vodafone-800 flex items-center space-x-1"
        >
          <DocumentTextIcon className="h-4 w-4" />
          <span>{link.document_id}</span>
          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
        </button>
        <span className="text-xs text-gray-500">({link.proposal_type})</span>
      </div>
      <div className="flex items-center space-x-2">
        <ConfidenceBadge confidence={link.confidence} />
        <MatchBasisBadge matchBasis={link.match_basis} />
      </div>
    </div>
    
    <p className="text-sm text-gray-700 mb-3 leading-relaxed">
      {link.proposal_text}
    </p>
    
    {link.linkage_reason && (
      <div className="bg-gray-50 rounded-md p-3">
        <p className="text-xs text-gray-600">
          <strong>Linkage Reason:</strong> {link.linkage_reason}
        </p>
      </div>
    )}
  </div>
);

const ConclusionRow = ({ conclusion, onViewDocument, isExpanded, onToggleExpand }) => (
  <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
    <div 
      className="bg-gray-50 px-6 py-4 cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={onToggleExpand}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <div className="flex items-center">
              {isExpanded ? (
                <ChevronDownIcon className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-5 w-5 text-gray-500" />
              )}
            </div>
            <h3 className="text-sm font-semibold text-gray-900">
              {conclusion.section_path}
            </h3>
            <span className="text-xs text-gray-500">
              ({conclusion.proposal_links.length} proposal{conclusion.proposal_links.length !== 1 ? 's' : ''})
            </span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed ml-8">
            {conclusion.conclusion_text}
          </p>
        </div>
      </div>
    </div>
    
    {isExpanded && (
      <div className="px-6 py-4 bg-white border-t border-gray-200">
        {conclusion.proposal_links.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {conclusion.proposal_links.map((link, index) => (
              <ProposalCard 
                key={index} 
                link={link} 
                onViewDocument={onViewDocument}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <InformationCircleIcon className="h-8 w-8 mx-auto mb-2" />
            <p className="text-sm">No proposals linked to this conclusion</p>
          </div>
        )}
      </div>
    )}
  </div>
);

const SubtopicSection = ({ subtopic, onViewDocument, expandedConclusions, onToggleConclusion }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <div className="mb-8">
      <div 
        className="flex items-center justify-between p-4 bg-vodafone-50 border border-vodafone-200 rounded-lg cursor-pointer hover:bg-vodafone-100 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-vodafone-600" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-vodafone-600" />
          )}
          <h2 className="text-lg font-semibold text-vodafone-900">
            {subtopic.subtopic_name}
          </h2>
        </div>
        <div className="flex items-center space-x-4 text-sm text-vodafone-700">
          <span>{subtopic.total_conclusions} conclusions</span>
          <span>{subtopic.total_proposals} proposals</span>
          <span>{subtopic.unique_documents_count} documents</span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="mt-4">
          {subtopic.conclusions.map((conclusion, index) => (
            <ConclusionRow
              key={conclusion.conclusion_id}
              conclusion={conclusion}
              onViewDocument={onViewDocument}
              isExpanded={expandedConclusions.has(conclusion.conclusion_id)}
              onToggleExpand={() => onToggleConclusion(conclusion.conclusion_id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ConclusionMapping = () => {
  const [subtopics, setSubtopics] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedConclusions, setExpandedConclusions] = useState(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState('');
  const [subtopicFilter, setSubtopicFilter] = useState('');

  useEffect(() => {
    loadCrossReferenceData();
  }, []);

  const loadCrossReferenceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading cross-reference data...');
      console.log('apiService methods:', Object.keys(apiService));
      
      // Test if apiService is working at all
      console.log('Testing basic API service...');
      const testResponse = await apiService.getSubtopics();
      console.log('Basic API test successful:', testResponse);
      
      // Load both subtopic data and metadata
      const [subtopicsResponse, metadataResponse] = await Promise.all([
        apiService.getCrossReferenceBySubtopic(),
        apiService.getCrossReferenceMetadata()
      ]);
      
      console.log('Subtopics response:', subtopicsResponse);
      console.log('Metadata response:', metadataResponse);
      
      setSubtopics(subtopicsResponse.data.subtopics || []);
      setMetadata(metadataResponse.data);
      
    } catch (error) {
      console.error('Error loading cross-reference data:', error);
      const errorMessage = handleApiError(error, 'Failed to load cross-reference data');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = (documentId) => {
    // This would typically open the document viewer
    toast.success(`Opening document ${documentId} - Feature coming soon!`);
  };

  const handleToggleConclusion = (conclusionId) => {
    const newExpanded = new Set(expandedConclusions);
    if (newExpanded.has(conclusionId)) {
      newExpanded.delete(conclusionId);
    } else {
      newExpanded.add(conclusionId);
    }
    setExpandedConclusions(newExpanded);
  };

  const filteredSubtopics = subtopics.filter(subtopic => {
    if (subtopicFilter && !subtopic.subtopic_name.toLowerCase().includes(subtopicFilter.toLowerCase())) {
      return false;
    }
    
    if (confidenceFilter) {
      // Check if any conclusions in this subtopic have proposals with the selected confidence
      return subtopic.conclusions.some(conclusion =>
        conclusion.proposal_links.some(link => 
          link.confidence.toLowerCase() === confidenceFilter.toLowerCase()
        )
      );
    }
    
    return true;
  });

  const getSummaryStats = () => {
    const totalConclusions = subtopics.reduce((sum, s) => sum + s.total_conclusions, 0);
    const totalProposals = subtopics.reduce((sum, s) => sum + s.total_proposals, 0);
    const totalDocuments = new Set(
      subtopics.flatMap(s => s.unique_documents)
    ).size;
    
    return { totalConclusions, totalProposals, totalDocuments };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        <div className="card animate-pulse">
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-charcoal-900 mb-2">Error Loading Cross-Reference Data</h3>
        <p className="text-charcoal-600 mb-4">{error}</p>
        <button onClick={loadCrossReferenceData} className="btn-vodafone">
          Try Again
        </button>
      </div>
    );
  }

  const stats = getSummaryStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-charcoal-900 mb-2">
          Cross-Reference Analysis
        </h1>
        <p className="text-charcoal-600">
          Mapping between RAN2#128 Report conclusions and semantic proposals across all subtopics
        </p>
      </div>

      {/* Analysis Metadata */}
      {metadata && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 mb-1">Analysis Information</h3>
              <div className="text-xs text-blue-700 space-y-1">
                <p><strong>Model:</strong> {metadata.model_used}</p>
                <p><strong>Analysis Date:</strong> {new Date(metadata.analysis_timestamp).toLocaleDateString()}</p>
                <p><strong>Report Source:</strong> RAN2#128 Report - Directory Listing /ftp/tsg_ran/WG2_RL2/TSGR2_128/Report</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-vodafone-600">{stats.totalConclusions}</div>
          <div className="text-sm text-gray-600">Total Conclusions</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">{stats.totalProposals}</div>
          <div className="text-sm text-gray-600">Linked Proposals</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{stats.totalDocuments}</div>
          <div className="text-sm text-gray-600">Referenced Documents</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{subtopics.length}</div>
          <div className="text-sm text-gray-600">Subtopics Analyzed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
        <FunnelIcon className="h-5 w-5 text-gray-500" />
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Confidence:</label>
          <select
            value={confidenceFilter}
            onChange={(e) => setConfidenceFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700">Subtopic:</label>
          <input
            type="text"
            placeholder="Filter by subtopic..."
            value={subtopicFilter}
            onChange={(e) => setSubtopicFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 w-64"
          />
        </div>
        {(confidenceFilter || subtopicFilter) && (
          <button
            onClick={() => {
              setConfidenceFilter('');
              setSubtopicFilter('');
            }}
            className="text-sm text-vodafone-600 hover:text-vodafone-800"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Subtopics */}
      <div>
        {filteredSubtopics.length > 0 ? (
          filteredSubtopics.map((subtopic) => (
            <SubtopicSection
              key={subtopic.subtopic_name}
              subtopic={subtopic}
              onViewDocument={handleViewDocument}
              expandedConclusions={expandedConclusions}
              onToggleConclusion={handleToggleConclusion}
            />
          ))
        ) : (
          <div className="text-center py-12 text-gray-500">
            <InformationCircleIcon className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Results Found</h3>
            <p>Try adjusting your filters to see more results.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConclusionMapping; 